import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

// Password strength checker
function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '#e5e7eb' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const levels = [
    { label: '', color: '#e5e7eb' },
    { label: 'Weak', color: '#ef4444' },
    { label: 'Fair', color: '#f97316' },
    { label: 'Good', color: '#eab308' },
    { label: 'Strong', color: '#22c55e' },
    { label: 'Very strong', color: '#16a34a' },
  ];
  return { score, ...levels[Math.min(score, 5)] };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenState, setTokenState] = useState('validating'); // 'validating' | 'valid' | 'invalid'
  const [tokenEmail, setTokenEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = getPasswordStrength(password);
  const passwordsMatch = password && confirm && password === confirm;
  const passwordMismatch = confirm && password !== confirm;

  // Validate token on mount
  useEffect(() => {
    if (!token) { setTokenState('invalid'); return; }
    api.get(`/auth/validate-reset-token?token=${token}`)
      .then(({ data }) => {
        if (data.valid) {
          setTokenState('valid');
          setTokenEmail(data.email);
        } else {
          setTokenState('invalid');
        }
      })
      .catch(() => setTokenState('invalid'));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password) { setError('Please enter a new password.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      // Auto-redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  }

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a2535 0%, #1a3a5c 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: 36, width: '100%', maxWidth: 420,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  };
  const inputStyle = {
    width: '100%', padding: '11px 14px', fontSize: 14, borderRadius: 8,
    border: '1px solid #d0d7de', outline: 'none', boxSizing: 'border-box',
  };
  const btnStyle = (disabled) => ({
    width: '100%', padding: 13, background: disabled ? '#9ca3af' : '#1a2535',
    color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', marginTop: 6,
  });

  return (
    <div style={containerStyle}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/jobhub-logo-dark.png" alt="JobHub" style={{ height: 100, width: 'auto', objectFit: 'contain', marginBottom: 8 }} />
          <p style={{ color: '#7aa3c0', fontSize: 14, margin: 0 }}>Job costing for electrical contractors</p>
        </div>

        <div style={cardStyle}>

          {/* Validating */}
          {tokenState === 'validating' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #1a2535', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: '#6b7280', fontSize: 14 }}>Validating your reset link...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Invalid token */}
          {tokenState === 'invalid' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 60, height: 60, background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 20px' }}>
                ⏱
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Link expired or invalid</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
                This password reset link has either expired or already been used.
                Reset links are valid for 1 hour and can only be used once.
              </p>
              <Link
                to="/forgot-password"
                style={{ display: 'block', padding: 13, background: '#1a2535', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 600, textAlign: 'center', marginBottom: 12 }}
              >
                Request a new reset link
              </Link>
              <Link to="/login" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
                ← Back to sign in
              </Link>
            </div>
          )}

          {/* Success */}
          {tokenState === 'valid' && done && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 20px' }}>
                ✅
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>Password reset!</h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 8px' }}>
                Your password has been changed successfully.
              </p>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>
                Redirecting you to sign in...
              </p>
              <Link
                to="/login"
                style={{ display: 'block', padding: 13, background: '#1a2535', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 600, textAlign: 'center' }}
              >
                Sign in now
              </Link>
            </div>
          )}

          {/* Reset form */}
          {tokenState === 'valid' && !done && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>Set a new password</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                  For <strong>{tokenEmail}</strong>
                </p>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* New password field */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                    New password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...inputStyle, paddingRight: 44 }}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9ca3af', padding: 2 }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>

                  {/* Strength meter */}
                  {password && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                        {[1,2,3,4,5].map(i => (
                          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= strength.score ? strength.color : '#e5e7eb', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      {strength.label && (
                        <div style={{ fontSize: 11, color: strength.color, fontWeight: 600 }}>{strength.label}</div>
                      )}
                    </div>
                  )}

                  {/* Requirements checklist */}
                  {password && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {[
                        { check: password.length >= 8, text: 'At least 8 characters' },
                        { check: /[A-Z]/.test(password), text: 'One uppercase letter' },
                        { check: /[0-9]/.test(password), text: 'One number' },
                        { check: /[^A-Za-z0-9]/.test(password), text: 'One special character (optional but recommended)' },
                      ].map(({ check, text }) => (
                        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <span style={{ color: check ? '#16a34a' : '#d1d5db', fontSize: 14 }}>{check ? '✓' : '○'}</span>
                          <span style={{ color: check ? '#374151' : '#9ca3af' }}>{text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm password field */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                    Confirm new password
                  </label>
                  <input
                    style={{ ...inputStyle, borderColor: passwordMismatch ? '#fca5a5' : passwordsMatch ? '#86efac' : '#d0d7de' }}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repeat your new password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                  />
                  {passwordMismatch && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>Passwords do not match</div>
                  )}
                  {passwordsMatch && (
                    <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>✓ Passwords match</div>
                  )}
                </div>

                <button
                  type="submit"
                  style={btnStyle(loading || !passwordsMatch || password.length < 8)}
                  disabled={loading || !passwordsMatch || password.length < 8}
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to="/login" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
                  ← Back to sign in
                </Link>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
